/**
 * 科目・キャラクター・アイテム・ガチャ・回収の定義(docs/19 §3・§6・§9)と、リグの既定(docs/19 §4)。
 *
 * どれも公開版リリース(docs/20 §2)の `catalog/*.json` に載るデータ。
 * 描画コードはここに置かない(リグの描画は apps/web。docs/19 §4.2)。
 */
import type { GachaCurrency, GachaPoolEntry, ItemEffect, ItemOrigin, Rarity } from './legacy.js';

/** ロケール → 文字列(`ja` は必須。ひらがな優先)。 */
export type LocalizedText = Readonly<Record<string, string>>;

/**
 * 名前の部品(docs/19 §9 の `nameParts`)。
 *
 * プレイヤーが付け直す名前は `parts: string[]` で保存し、各 part は**この `id`** を指す
 * (表示文字列を保存すると表記を直したときに検証が通らなくなるため)。
 */
export interface LocalizedWord {
  readonly id: string;
  readonly text: LocalizedText;
}

// ── 科目 ─────────────────────────────────────────────────

export const SUBJECT_STATUSES = ['active', 'paused'] as const;
export type SubjectStatus = (typeof SUBJECT_STATUSES)[number];

export interface SubjectDef {
  /** 固定。作成後に変えない(通貨キーと同じ普遍領域ID。docs/19 §2)。 */
  readonly id: string;
  readonly name: LocalizedText;
  /** 通貨キー。原則 id と同じ。 */
  readonly currency: GachaCurrency;
  readonly characterId: string;
  readonly color: string;
  readonly icon: string;
  readonly order: number;
  /** paused は新規の入口から外す。持ち物・進捗は消さない。 */
  readonly status: SubjectStatus;
}

/**
 * 通貨のうち、科目に属さない**特別通貨**(docs/19 §2: `trophy` は科目ではない)。
 * 特別通貨のガチャは、どの科目にぶら下げても通貨の一致を求めない。
 */
export const SPECIAL_CURRENCIES: readonly GachaCurrency[] = ['trophy'];

// ── リグ ─────────────────────────────────────────────────

export const RIG_TYPES = ['legacy-emoji', 'pixel-layered'] as const;
export type RigType = (typeof RIG_TYPES)[number];

export type SlotKind = 'base' | 'equip';

export interface SlotDef {
  readonly id: string;
  readonly name: LocalizedText;
  /** base=素体(最初から全部選べる) / equip=ガチャで集める。 */
  readonly kind: SlotKind;
  /** true は外せない(pixel-layered の body/head/eyes)。 */
  readonly required: boolean;
}

/** リグが知っている枠の既定(キャラクターはここから選び、名前・必須を調整する)。 */
export interface RigDefinition {
  readonly type: RigType;
  readonly slots: readonly SlotDef[];
  /** アイテムの `art.kind` として許すもの。 */
  readonly artKind: ItemArt['kind'];
  /** `CharacterDef.bases`(絵文字の素体)を使うリグか。 */
  readonly usesBases: boolean;
  /** `art.companions[].slot` として許すもの(本体と同じIDの付属レイヤー)。 */
  readonly companionSlots: readonly string[];
}

/**
 * `legacy-emoji`(現行アバター。docs/19 §4.1)。6枠すべて equip、必須なし。
 * 名前は apps/web の `SLOT_LABELS` と同じ。
 */
export const LEGACY_EMOJI_RIG: RigDefinition = {
  type: 'legacy-emoji',
  artKind: 'emoji',
  usesBases: true,
  companionSlots: [],
  slots: [
    { id: 'hat', name: { ja: 'ぼうし' }, kind: 'equip', required: false },
    { id: 'face', name: { ja: 'かお' }, kind: 'equip', required: false },
    { id: 'outfit', name: { ja: 'ふく' }, kind: 'equip', required: false },
    { id: 'hand', name: { ja: 'てにもつ' }, kind: 'equip', required: false },
    { id: 'background', name: { ja: 'はいけい' }, kind: 'equip', required: false },
    { id: 'effect', name: { ja: 'エフェクト' }, kind: 'equip', required: false },
  ],
};

/**
 * `pixel-layered`(PixelFantasy 型。docs/19 §4.2・§8)。
 * 素体 = body head eyes ears hair(body/head/eyes は必須)、それ以外は装備。
 * 枠名は既定の仮名(キャラクター側で付け直せる)。
 */
export const PIXEL_LAYERED_RIG: RigDefinition = {
  type: 'pixel-layered',
  artKind: 'atlas',
  usesBases: false,
  companionSlots: ['arms', 'bracers'],
  slots: [
    { id: 'body', name: { ja: 'からだ' }, kind: 'base', required: true },
    { id: 'head', name: { ja: 'あたま' }, kind: 'base', required: true },
    { id: 'eyes', name: { ja: 'め' }, kind: 'base', required: true },
    { id: 'ears', name: { ja: 'みみ' }, kind: 'base', required: false },
    { id: 'hair', name: { ja: 'かみ' }, kind: 'base', required: false },
    { id: 'armor', name: { ja: 'よろい' }, kind: 'equip', required: false },
    { id: 'helmet', name: { ja: 'かぶと' }, kind: 'equip', required: false },
    { id: 'weapon', name: { ja: 'ぶき' }, kind: 'equip', required: false },
    { id: 'shield', name: { ja: 'たて' }, kind: 'equip', required: false },
    { id: 'back', name: { ja: 'せなか' }, kind: 'equip', required: false },
    { id: 'cape', name: { ja: 'マント' }, kind: 'equip', required: false },
    { id: 'mask', name: { ja: 'おめん' }, kind: 'equip', required: false },
  ],
};

export const RIGS: Readonly<Record<RigType, RigDefinition>> = {
  'legacy-emoji': LEGACY_EMOJI_RIG,
  'pixel-layered': PIXEL_LAYERED_RIG,
};

/**
 * pixel-layered の描画順(下→上。docs/19 §4.2)。描画は apps/web が行うが、
 * レイヤー名の一次情報としてここに置く(アトラス取込・検証で使う)。
 */
export const PIXEL_LAYER_ORDER = [
  'cape',
  'back',
  'shield',
  'body',
  'armor',
  'head',
  'eyes',
  'mask',
  'hair',
  'ears',
  'helmet',
  'arms',
  'bracers',
  'weapon',
] as const;

// ── キャラクター ─────────────────────────────────────────

export const CHARACTER_STATUSES = ['active', 'paused', 'retired'] as const;
export type CharacterStatus = (typeof CHARACTER_STATUSES)[number];

/**
 * 絵文字の素体(`legacy-emoji` リグ専用。docs/19 §4.1「絵文字ベース5種」)。
 * 現行 apps/web の `BASE_CHARACTERS` と同じ形。
 */
export interface EmojiBaseDef {
  readonly id: string;
  readonly name: LocalizedText;
  readonly emoji: string;
  /** 背景のアクセント色。 */
  readonly color: string;
}

export interface CharacterLook {
  /** legacy-emoji の素体(`bases[].id`)。 */
  readonly base?: string;
  readonly equipped: Readonly<Record<string, string>>;
}

export interface CharacterNameParts {
  readonly prefixes: readonly LocalizedWord[];
  readonly nouns: readonly LocalizedWord[];
}

export interface CharacterDef {
  readonly id: string;
  readonly name: LocalizedText;
  readonly rigType: RigType;
  /** リグの既定から選ぶ・並べ替える。 */
  readonly slots: readonly SlotDef[];
  readonly defaultLook: CharacterLook;
  /** プレイヤーが付け直す名前の候補(docs/19 §9)。 */
  readonly nameParts: CharacterNameParts;
  /** 絵文字の素体(`legacy-emoji` のみ。ほかのリグは持たない)。 */
  readonly bases?: readonly EmojiBaseDef[];
  readonly status: CharacterStatus;
}

// ── アイテム ─────────────────────────────────────────────

export interface ItemArtEmoji {
  readonly kind: 'emoji';
  readonly emoji: string;
}

export interface ItemArtAtlas {
  readonly kind: 'atlas';
  /** R2 上のパス(内容ハッシュ付き)。 */
  readonly sheet: string;
  readonly atlas: string;
  readonly icon: string;
  /** arms・bracers など本体と同じIDの付属レイヤー。 */
  readonly companions?: readonly {
    readonly slot: string;
    readonly sheet: string;
    readonly atlas: string;
  }[];
  readonly flags?: { readonly showEars?: boolean };
}

export type ItemArt = ItemArtEmoji | ItemArtAtlas;

export const ITEM_STATUSES = ['active', 'retired'] as const;
export type ItemStatus = (typeof ITEM_STATUSES)[number];

/** 既存 ItemDef の拡張(docs/19 §3)。既存項目は維持。 */
export interface ItemDef {
  readonly id: string;
  readonly slot: string;
  readonly rarity: Rarity;
  readonly name: LocalizedText;
  readonly effects: readonly ItemEffect[];
  readonly services: readonly string[];
  /** 出どころ。省略は `gacha`。 */
  readonly origin?: ItemOrigin;
  /** 旧来の画像差し替え用(相対パス)。現行データには無い。 */
  readonly image?: string;
  readonly characterId: string;
  /** true は全員が所持扱い(所持記録を作らない)。`kind:'base'` の枠だけ。 */
  readonly starter?: boolean;
  readonly art: ItemArt;
  readonly status: ItemStatus;
}

// ── ガチャ ───────────────────────────────────────────────

export const GACHA_STATUSES = ['active', 'paused', 'retired'] as const;
export type GachaStatus = (typeof GACHA_STATUSES)[number];

/** 既存 GachaDef の拡張(docs/19 §3)。 */
export interface GachaDef {
  readonly id: string;
  readonly name: LocalizedText;
  readonly currency: GachaCurrency;
  readonly cost: number;
  readonly cost10: number;
  readonly pool: readonly GachaPoolEntry[];
  readonly period: { readonly from: string | null; readonly to: string | null };
  readonly subjectId: string;
  /** プールは全てこのキャラクターのアイテム。 */
  readonly characterId: string;
  readonly rarityRates: Readonly<Record<Rarity, number>>;
  readonly guarantee: { readonly rarity: Rarity; readonly count: number };
  readonly duplicateConvertCoins: Readonly<Record<Rarity, number>>;
  readonly status: GachaStatus;
}

/**
 * ガチャの標準値(新しいガチャの初期値。docs/02 §7.2・docs/19 §5・§8)。
 * 排出率・保証・重複変換は functions/src/config.ts の `GACHA_RULES` と同じ値
 * (functions の `gachaEconomyParity.test.ts` が一致を保証する)。価格は算数ガチャと同じ 1回100 / 10連1,000。
 */
export const DEFAULT_GACHA_ECONOMY = {
  cost: 100,
  cost10: 1000,
  rarityRates: { N: 0.7, R: 0.25, SR: 0.045, UR: 0.005 },
  guarantee: { rarity: 'R', count: 10 },
  duplicateConvertCoins: { N: 10, R: 30, SR: 100, UR: 300 },
} as const satisfies Pick<
  GachaDef,
  'cost' | 'cost10' | 'rarityRates' | 'guarantee' | 'duplicateConvertCoins'
>;

// ── 回収 ─────────────────────────────────────────────────

/** 廃止アイテムの回収表(docs/19 §6)。 */
export interface RetirementDef {
  readonly id: string;
  readonly itemIds: readonly string[];
  readonly refund: { readonly currency: GachaCurrency; readonly amountPerItem: number };
  readonly reason: string;
}

// ── 公開版の catalog/*.json(追加3ファイル)─────────────────

export interface SubjectFile {
  readonly schemaVersion: number;
  readonly subjects: readonly SubjectDef[];
}

export interface CharacterFile {
  readonly schemaVersion: number;
  readonly characters: readonly CharacterDef[];
}

export interface RetirementFile {
  readonly schemaVersion: number;
  readonly retirements: readonly RetirementDef[];
}
